// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Fullscreen DOCX viewer modal for contract-negotiation. Opens to a specific
 * page (when launched from a `p. N` link) and uses the same DocxView body as
 * the inline Document tab — highlighted quotes + side-rail comment list.
 */
import type { ContractDuplicateFinding, TroublesomeClauseFinding } from '../hooks/extractors'
import type { NegotiationFinding } from './NegotiationLayout'

export interface DocxAnnotation {
  id: string
  /** Text to locate in the rendered DOCX. */
  searchText: string
  /** Comment body shown in the side rail. */
  comment: string
  /** Section / clause label shown above the comment. */
  title: string
  severity: 'high' | 'medium' | 'low'
  pageNumber?: number
}

// Helpers for unified annotation construction from finding types.
export function negotiationFindingToAnnotation(f: NegotiationFinding, index: number): DocxAnnotation | null {
  const searchText = (f.contractText || '').trim()
  if (!searchText) return null
  const severity: DocxAnnotation['severity'] = f.status === 'reject' ? 'high' : 'medium'
  const summary = f.summary ?? `Finding ${index + 1}`
  const title = f.sectionReference ? `${f.sectionReference} — ${summary}` : summary
  const comment = [f.summary, f.recommendation].filter(Boolean).join('\n\n')
  return {
    id: `neg-${index}`,
    searchText,
    title,
    comment,
    severity,
    pageNumber: f.pageNumber ?? undefined,
  }
}

export function clauseFindingToAnnotation(f: TroublesomeClauseFinding, index: number): DocxAnnotation | null {
  const searchText = (f.quotedText || '').trim()
  if (!searchText) return null
  const title = f.sectionReference ? `${f.sectionReference} — ${f.issue}` : f.issue
  const comment = [f.summary, f.recommendedModification, f.reason].filter(Boolean).join('\n\n')
  return {
    id: `clause-${index}`,
    searchText,
    title,
    comment,
    severity: 'high',
    pageNumber: f.pageNumber ?? undefined,
  }
}

const DUPLICATE_CATEGORY_LABEL: Record<ContractDuplicateFinding['category'], string> = {
  TRUE_DUPLICATE: 'Duplicate',
  CONFLICT_OVERRIDE: 'Conflict',
  FLOW_DOWN_INCONSISTENCY: 'Flow-down',
  CLEANUP: 'Cleanup',
}

export function duplicateFindingToAnnotations(
  f: ContractDuplicateFinding,
  index: number,
): DocxAnnotation[] {
  if (!f.excerpts.length) return []
  const severity: DocxAnnotation['severity'] =
    f.severity === 'HIGH' ? 'high' : f.severity === 'MEDIUM' ? 'medium' : 'low'
  const categoryLabel = DUPLICATE_CATEGORY_LABEL[f.category]
  const comment = [f.explanation, f.consolidation].filter(Boolean).join('\n\n')
  return f.excerpts
    .map((excerpt, i) => {
      const searchText = excerpt.trim()
      if (!searchText) return null
      const location = f.locations[i] ?? f.locations[0] ?? ''
      const title = location ? `${location} — ${categoryLabel}` : categoryLabel
      return {
        id: `dup-${index}-${i}`,
        searchText,
        title,
        comment,
        severity,
      } satisfies DocxAnnotation
    })
    .filter((a): a is DocxAnnotation => a !== null)
}
